import { Box, Chip, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { CheckedIcon, SkillsIcon, UnCheckedIcon } from "../../../assets/icons/MUI/AdvancedSearch";
import { colors } from "../../../theme/palette";
import SectionHeader from "./Components/SectionHeader";
import FilterBox from "./Components/FilterBox";
import { City, Country, ScoutFilters, Skill } from "../../../types";
import { fetchCountries, getAdvancedSearchLists, getSkillsLists } from "../../../api/network";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

type Props = {};

type ScoutType = {
  id: number;
  name: string;
};

type Filters = {
  countryOfScouting: number | null;
  cityOfScouting: number | null;
  typeOfScouting: string;
  skills: number[];
};

const ScoutFilter = ({
  advancedFilters,
  setAdvancedFilters,
  isFilterCleared,
}: {
  advancedFilters: ScoutFilters;
  setAdvancedFilters: React.Dispatch<React.SetStateAction<ScoutFilters>>;
  isFilterCleared: boolean;
}) => {
  const { t } = useTranslation();

  const initialFilters: Filters = {
    countryOfScouting: advancedFilters.countryOfScouting || null,
    cityOfScouting: advancedFilters.cityOfScouting || null,
    typeOfScouting: advancedFilters.typeOfScouting || "",
    skills: advancedFilters.skills || [],
  };
  const [cities, setCities] = useState<City[]>([]);
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    setAdvancedFilters(filters as ScoutFilters);
  }, [filters]);

  useEffect(() => {
    setFilters(initialFilters);
  }, [isFilterCleared]);

  const handleSkillClick = (skillId: number) => () => {
    setFilters((prevState) => {
      const isSkillSelected = prevState.skills.includes(skillId);
      const newSkills = isSkillSelected
        ? prevState.skills.filter((id) => id !== skillId)
        : [...prevState.skills, skillId];
      return { ...prevState, skills: newSkills };
    });
  };

  const { data: skills } = useQuery<Skill[]>({
    queryKey: ["scoutSkills"],
    queryFn: () => getSkillsLists("scout"),
    initialData: [] as Skill[],
    staleTime: 0,
  });

  const { data: scoutTypes } = useQuery<ScoutType[]>({
    queryKey: ["scoutType"],
    queryFn: () => getAdvancedSearchLists("scoutType"),
    initialData: [] as ScoutType[],
    staleTime: 0,
  });

  const { data: countries } = useQuery<Country[]>({
    queryKey: ["countries"],
    queryFn: () => fetchCountries(),
    initialData: [] as Country[],
    staleTime: 0,
  });

  useEffect(() => {
    const selectedCountry = countries.find((country) => country.id === filters.countryOfScouting);
    const citiesResults = selectedCountry?.cities || [];
    setCities(citiesResults);
  }, [countries]);

  return (
    <>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.countriesCoachedIn")}>
        <Select
          value={filters.countryOfScouting?.toString() || ""}
          onChange={(event: SelectChangeEvent<string>) => {
            const selectedCountryId = parseInt(event.target.value, 10) || null;
            const selectedCountry = countries.find((country) => country.id === selectedCountryId);
            const citiesResults = selectedCountry?.cities || [];

            // Set the new cities list
            setCities(citiesResults);

            // Check if the current cityOfScouting exists in the new cities list
            const isCityValid = citiesResults.some(
              // @ts-ignore
              (city) => city.id === filters.cityOfScouting
            );

            setFilters((prevState) => ({
              ...prevState,
              countryOfScouting: selectedCountryId,
              cityOfScouting: isCityValid ? prevState.cityOfScouting : null, // Reset only if the city ID doesn't match
            }));
          }}
          sx={{ minWidth: "160px" }}
        >
          {countries.map((country) => (
            <MenuItem key={country.id} value={country.id.toString()}>
              {country.name}
            </MenuItem>
          ))}
        </Select>
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.cityOfScouting")}>
        <Select
          value={filters.cityOfScouting?.toString() || ""}
          onChange={(event: SelectChangeEvent<string>) =>
            setFilters((prevState) => ({
              ...prevState,
              cityOfScouting: parseInt(event.target.value, 10) || null,
            }))
          }
          disabled={!filters.countryOfScouting}
          sx={{ minWidth: "160px" }}
        >
          {cities.map((city) => (
            <MenuItem key={city.id} value={city.id.toString()}>
              {city.name}
            </MenuItem>
          ))}
        </Select>
      </FilterBox>
      <FilterBox label={t("components.networkController.advancedFilterPopUp.coachType")}>
        <Select
          value={filters.typeOfScouting}
          onChange={(event: SelectChangeEvent<string>) =>
            setFilters((prevState) => ({
              ...prevState,
              typeOfScouting: event.target.value as string,
            }))
          }
          displayEmpty
          sx={{ minWidth: "160px" }}
        >
          {scoutTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
      </FilterBox>
      <SectionHeader icon={SkillsIcon} title={t("components.networkController.advancedFilterPopUp.skills")} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 12px",
        }}
      >
        {skills.map((skill) => (
          <Chip
            key={skill.id}
            color={filters.skills.includes(skill.id) ? "primary" : "secondary"}
            label={skill.name}
            icon={
              filters.skills.includes(skill.id) ? (
                <CheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              ) : (
                <UnCheckedIcon style={{ fill: "none", width: "10px", height: "10px" }} />
              )
            }
            onClick={handleSkillClick(skill.id)}
            sx={{
              padding: "5px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: "400px",
              ...(filters.skills.includes(skill.id) ? {} : { backgroundColor: colors.gray50, color: colors.black }),
            }}
          />
        ))}
      </Box>
    </>
  );
};

export default ScoutFilter;
